import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ConfirmationPopup from "../../components/ConfirmationPopup/ConfirmationPopup";
import IconButton from "../../components/IconButton/IconButton";
import PrimaryButton from "../../components/PrimaryButton/PrimaryButton";
import SecondaryButton from "../../components/SecondaryButton/SecondaryButton";
import PageWrapper from "../PageWrapper/PageWrapper";
import deleteIcon from "../../assets/images/bin.svg";
// import addMeetingIcon from "../../assets/images/add-meeting.svg";
import editIcon from "../../assets/images/pen.svg";
import Card from "../../components/Card/Card";
import { Icon } from "../../components/Icon/Icon";
import { Modal } from "../../components/Modal/Modal";
import { FormType } from "../../shared/form.type";
import FilterPill from "../../components/FilterPill/FilterPill";
import CustomerForm from "../../components/CustomerForm/CustomerForm";
import CustomersAPI, { Customer } from "../../api/CustomersAPI";
import { useGetCustomers } from "../../hooks/useGetCustomers";
import usePermissionFlag from "../../hooks/usePermissionFlag";
import EmployeesAPI, { Employee } from "../../api/EmployeesAPI";
import { customerStatusToPillColor, customerStatusToTranslationKey, getCustomerStatuses } from "../../shared/customerStatusToTranslationKey";
import { Autocomplete } from "../../components/Autocomplete/Autocomplete";
import CustomerSearchResult from "../../components/CustomerSearchResult/CustomerSearchResult";
import { PermissionFlags } from "../../components/PermissionFlags/PermissionFlags";
import Pill from "../../components/Pill/Pill";
import { CancelToken } from "../../api/SimpleAPIClient";
import MeetingForm from "../../components/MeetingForm/MeetingForm";
import { Meeting } from "../../api/MeetingsAPI";
import NoDataCTA from "../../components/NoDataCTA/NoDataCTA";
import Table, { ColumnDefinition } from "../../components/Table/Table";
import { useNavigate } from "react-router-dom";
import Switch from "../../components/Switch/Switch";
import funnelIcon from "../../assets/images/funnel.svg";
import xMarkIcon from "../../assets/images/x-mark.svg";
import infoIcon from "../../assets/images/info.svg";
import Tooltip from "../../components/Tooltip/Tooltip";
import { parseLegacyDateArray, safeParseDate } from "../../shared/timeUtils";

export type CustomerColumn = ColumnDefinition<Customer>;

const EMPTY_CUSSTOMER: Customer = {
  name: '',
  surname: '',
  countryCode: 'de',
  status: 'NEW',
  attachments: [],
  notes: '',
};

const EMPTY_MEETING: Meeting = {
  customerName: '',
  customerSurname: '',
  startTime: new Date().toISOString(),
  endTime: new Date().toISOString(),
  type: 'VISIT',
  reminder: false,
  disable: false,
  weddingDate: null,
  phone: null,
  lastUpdateTime: null,
  notes: null,
  title: null
};

const loadEnrichedCustomerOrFallback = async (customer: Customer): Promise<Customer> => {
  if (customer.id) {
    try {
      const response = await CustomersAPI.getById(customer.id);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch customer details:', error);
      return customer;
    }
  }
  return customer;
};

const CustomersPage = () => {
  const [action, setAction] = useState<FormType>('READ');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [filters, setFilters] = useState({ employee: "", status: "" });
  const [followUpOnly, setFollowUpOnly] = useState(false);
  const [searchIsLoading, setSearchIsLoading] = useState(false);
  const [showMeetingForm, setShowMeetingForm] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const { isLoading, customers, pagination, addFn, updateFn, removeFn, setPage } = useGetCustomers({ ...filters, followUpOnly });
  const { hasAccess: canEditCustomers } = usePermissionFlag(['edit-customers']);

  const navigate = useNavigate(); 
  const { t } = useTranslation();

  const search = (input: string, resultsSize: number, setResultsFn: any) => {
    setSearchIsLoading(true);
    CustomersAPI.search(input).then((resp) => {
      const raw = resp.data.content;
      const respRaw = raw.slice(0, resultsSize);
      setSearchIsLoading(false);
      setResultsFn(respRaw);
    });
    return CancelToken.source().cancel("Cancelling...");
  };

  const load = async () => {
    const employeesResp = await EmployeesAPI.getSalesRepresentatives();
    setEmployees(employeesResp.data.map(s => s));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (filterType: 'employee', value: Employee) => {
    setFilters((prevFilters) => {
      const updatedFilters = { ...prevFilters };
      if (updatedFilters[filterType] === String(value.id)) {
        updatedFilters[filterType] = '';
    } else {
        updatedFilters[filterType] = String(value.id!);
    }
      return updatedFilters;
    });
  };

  const setStatus = (status: string) => {
    setFilters(prev => {
      if (prev.status === status) {
        return { ...prev, status: '' };
      } else {
        return { ...prev, status };
      }
    });
  }

  const isFiltering = !!filters.employee || !!filters.status;

  const actionButton = (action: FormType) => {
    switch (action) {
      case 'CREATE':
        return <span className="flex justify-center"><PrimaryButton text={t("global.create")!} fn={() => registerAndClose(selectedCustomer!)} /></span>;
      case 'UPDATE':
        return <span className="flex justify-center"><PrimaryButton text={t("global.update")!} fn={() => registerAndClose(selectedCustomer!)} /></span>;
      default:
        return;
    }
  };

  const registerAndClose = async (e: Customer) => {
    if (!!e) {
      if (action === 'CREATE') {
        addFn(e);
      } else {
        updateFn(e);
      }
      setSelectedCustomer(null);
      setAction("READ");
    }
  }

  // const openMeetingForm = (customer: Customer) => {
  //   setShowMeetingForm(false);
  // }

  const viewCustomerDetails = async (customer: Customer) => {
    const enrichedCustomer = await loadEnrichedCustomerOrFallback(customer);
    setSelectedCustomer(enrichedCustomer);
    
    if (canEditCustomers) {
      setAction('UPDATE');
    } else {
      setAction('READ');
    }
  }

  const editCustomer = async (customer: Customer) => {
    const enrichedCustomer = await loadEnrichedCustomerOrFallback(customer);
    setSelectedCustomer(enrichedCustomer);
    setAction('UPDATE');
  }

  const customerColumns: CustomerColumn[] = [
    {
      key: 'name',
      title: t("customers.details.name") || "Name",
      render: (customer) => (
        <div className="flex items-center space-x-2">
          <p 
            className="font-medium cursor-pointer hover:text-blue-600 transition-colors truncate"
            onClick={() => viewCustomerDetails(customer)}
          >
            {`${customer.name} ${customer.surname}` || 'Unnamed Customer'}
          </p>
        </div>
      ),
    },
    {
      key: 'status',
      title: t("customers.details.status") || "Status",
      render: (customer) => (
        <div className="w-16 text-center">
          <Pill 
            text={t(customerStatusToTranslationKey(customer.status))}
            color={customerStatusToPillColor(customer.status)}
          />
        </div>
      ),
    },
    {
      key: 'weddingDate',
      title: t("customers.details.wedding_date") || "Wedding Date",
      render: (customer) => {
        let date: Date | null = null;
        if (customer.weddingDate) {
          if (Array.isArray(customer.weddingDate)) {
            date = parseLegacyDateArray(customer.weddingDate);
          } else {
            date = safeParseDate(customer.weddingDate);
          }
        }
        return (
          <div className="w-20">
            <span className="text-sm text-gray-600 truncate block">
              {date ? date.toLocaleDateString() : '-'}
            </span>
          </div>
        );
      },
    },
    {
      key: 'phone',
      title: t("customers.details.phone") || "Phone",
      render: (customer) => (
        <div className="max-w-xs">
          <span className="text-sm text-gray-600 truncate block">
            {customer.phone || '-'}
          </span>
        </div>
      ),
    },
    {
      key: 'email',
      title: t("customers.details.email") || "Email",
      render: (customer) => (
        <div className="max-w-xs">
          <span className="text-sm text-gray-600 truncate block">
            {customer.email || '-'}
          </span>
        </div>
      ),
    },
  ];

  const renderActions = (customer: Customer): React.ReactNode => {
    return (
      <div className="flex justify-center space-x-1">
        {/* <PermissionFlags requiredFlags={['edit-customers'] || []}>
          <IconButton onClick={() => openMeetingForm(customer)}>
            <Icon url={addMeetingIcon} cssStyles="w-5" />
          </IconButton>
        </PermissionFlags> */}
        <PermissionFlags requiredFlags={['edit-customers'] || []}>
          <IconButton onClick={() => editCustomer(customer)}>
            <Icon url={editIcon} cssStyles="w-5" />
          </IconButton>
        </PermissionFlags>
        <PermissionFlags requiredFlags={['remove-customers'] || []}>
          <IconButton>
            <ConfirmationPopup 
              onConfirm={() => removeFn(customer)} 
              confirmText={t("global.confirm") || "Confirm"} 
              title={`${t("global.remove") || "Remove"}: ${customer.name}`}
            >
              <Icon url={deleteIcon} cssStyles="w-5" />
            </ConfirmationPopup>
          </IconButton>
        </PermissionFlags>
      </div>
    );
  };

  return (
    <PageWrapper>
      <div className="space-y-5">
        <div className="container flex flex-col md:flex-row my-3 items-stretch space-x-0 md:space-x-5">
          <Card>
            <>
              <div className="flex-col">
                <div className="md:flex md:flex-row md:items-center md:space-x-3">
                  <div className="container flex flex-wrap space-x-5 mb-2 md:mb-0">
                    <Autocomplete
                      resultsSize={15}
                      searchFn={search}
                      onSelect={() => null}
                      placeholder={t("customers.search.placeholder") || "Search customers..."} 
                      isLoading={searchIsLoading}
                      errorMessage={t("global.empty_results") || "No results found"}
                      toElementFn={(customer: Customer) => (
                        <CustomerSearchResult 
                          key={customer.id} 
                          customer={customer} 
                          onView={viewCustomerDetails} 
                        />
                      )}
                    />
                  </div>
                  <div className="hidden md:flex">
                    <SecondaryButton 
                      text={<span className="inline-flex items-center">{t("filters.button", "Filters")}{isFiltering && <span className="ml-1 inline-block w-2 h-2 rounded-full bg-amber-500"/>}</span>} 
                      fn={() => setShowFilters(!showFilters)}
                      icon={<Icon url={funnelIcon} cssStyles="w-4 h-4" />}
                    />
                  </div>
                </div>
                <span className="md:hidden">
                  <SecondaryButton 
                    text={<span className="inline-flex items-center">{t("filters.button", "Filters")}{isFiltering && <span className="ml-1 inline-block w-2 h-2 rounded-full bg-amber-500"/>}</span>} 
                    fn={() => setShowFilters(!showFilters)}
                    icon={<Icon url={funnelIcon} cssStyles="w-4 h-4" />}
                  />
                </span>
              </div>
              {showFilters && (
                <>
                  <div className="space-y-2 mt-2">
                    {/* Employee Section */}
                    <div className="space-y-3">
                      <h3 className="text-sm font-semibold text-gray-700">{t("customers.filters.employees", "Employees")}</h3>
                      <div className="container flex flex-wrap max-h-48 overflow-y-scroll">
                        {
                          employees.length > 0 ? employees.map((s, i) => <FilterPill isActive={filters?.employee === String(s.id)} key={i} text={s.name} fn={() => handleFilterChange("employee", s)} />)
                          : (
                            <p className="text-sm text-gray-500">{t("customers.filters.employees.empty", "No employees found with role sales representative")}</p>
                          )
                        }
                      </div>
                    </div>

                    {/* Status Section */}
                    <div className="space-y-3">
                      <h3 className="text-sm font-semibold text-gray-700">{t("customers.filters.status", "Status")}</h3>
                      <div className="container flex flex-wrap max-h-48 overflow-y-scroll">
                        {getCustomerStatuses().map((status, i) => (
                          <FilterPill 
                            key={i}
                            text={t(customerStatusToTranslationKey(status))}
                            isActive={filters?.status === status}
                            fn={() => setStatus(status)}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </>
              )}
              <div className="flex-col md:flex md:flex-row justify-between md:justify-start items-center space-x-4 mt-3">
                <PermissionFlags requiredFlags={['edit-customers']}>
                  <PrimaryButton text={t("global.create")!} fn={() => { setAction('CREATE');setSelectedCustomer(EMPTY_CUSSTOMER);}} />
                </PermissionFlags>
                {isFiltering && (
                  <SecondaryButton 
                    actionName="Show All Customers" 
                    text={t("filters.reset", "Reset Filters")!} 
                    fn={() => setFilters({ employee: '', status: '' })}
                    icon={
                      <span className="relative inline-block w-4 h-4">
                        <Icon url={funnelIcon} cssStyles="w-4 h-4" />
                        <span className="absolute -top-1 -right-1">
                          <Icon url={xMarkIcon} cssStyles="w-3 h-3" />
                        </span>
                      </span>
                    }
                    compact
                  />
                )}
              </div>
            </>
          </Card>
        </div>
        <Card>
          <>
            <div className="container">
              <div className="flex justify-end mb-4">
                <div className="flex items-center space-x-2">
                  <Tooltip 
                    content={t("customers.listing.follow_up_opportunities.tooltip", 
                      "Shows customers who had visits in the last 3-4 months and only have First Visit-type meetings. These customers may be ready for follow-up actions.")}
                    position="bottom"
                  >
                    <Icon url={infoIcon} cssStyles="w-4 h-4 text-blue-500 cursor-help"/>
                  </Tooltip>
                  <label htmlFor="followup-filter" className="text-sm font-medium text-gray-700">
                    {t("customers.listing.follow_up_opportunities", "Detect potential follow-up opportunities")}
                  </label>
                  <Switch
                    id="followup-filter"
                    checked={followUpOnly}
                    onChange={() => setFollowUpOnly(prev => !prev)}
                  />
                </div>
              </div>
              <Table<Customer>
                isLoading={isLoading}
                data={customers}
                columns={customerColumns}
                onRowClick={async (customer) => {
                  setAction('READ');
                  const enrichedCustomer = await loadEnrichedCustomerOrFallback(customer);
                  setSelectedCustomer(enrichedCustomer);
                }}
                emptyListComponent={
                  <NoDataCTA
                      headline={t("customers.empty.cta.title")}
                      subtext={t("customers.empty.cta.body")}
                      primaryActionText={t("customers.new_customer.button")}
                      onPrimaryAction={() => navigate('/calendar')}
                  />
                }
                isSelectable={false}
                selectedItems={[]}
                compareFunction={(item, selectedItem) => item.id === selectedItem.id}
                renderActions={renderActions}
              />
            </div>
            {pagination.totalPages > 1 && (
              <div className="flex justify-center mt-4 items-center gap-2">
                <SecondaryButton
                  text={t("customers.pagination.prev", "Prev") as string}
                  fn={() => setPage(Math.max(0, pagination.number - 1))}
                  isDisabled={pagination.number === 0}
                  actionName="Pagination Prev"
                />
                <span className="px-3 py-1 mx-1 text-sm text-gray-600">
                  {t("customers.pagination.page", "Page {{page}} of {{totalPages}}", { 
                    page: pagination.number + 1, 
                    totalPages: pagination.totalPages 
                  })}
                </span>
                <SecondaryButton
                  text={t("customers.pagination.next", "Next") as string}
                  fn={() => setPage(Math.min(pagination.totalPages - 1, pagination.number + 1))}
                  isDisabled={pagination.number >= pagination.totalPages - 1}
                  actionName="Pagination Next"
                />
              </div>
            )}
          </>
        </Card>
          { selectedCustomer && 
            <Modal modalTitle={selectedCustomer.name || "customers.form.new_customer"} canClose onClose={() => setSelectedCustomer(null)}>
              <CustomerForm customer={selectedCustomer} onCustomerChange={setSelectedCustomer} actionButton={actionButton(action)} isDisabled={action === 'READ'}/>
            </Modal>
          }
          {/* TODO: add Customer info and logic to MeetingForm */}
          { showMeetingForm && 
            <Modal modalTitle={selectedCustomer?.name || "customers.form.new_customer"} canClose onClose={() => {setSelectedCustomer(null); setShowMeetingForm(false);}}>
              <MeetingForm meeting={EMPTY_MEETING} onMeetingChange={() => null} actionButton={actionButton('CREATE')}/>
            </Modal>
          }
      </div>
    </PageWrapper>
  );
}

export default CustomersPage;
